import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilter, TFilters, TOrderBy } from 'redux/types';
import { getCustomsDutyRateListRequest } from 'redux/reducers/taxDuties/reducer';
import {
	getCustomsDutyRateList,
	getCustomsDutyRateListLoading,
} from 'redux/reducers/taxDuties/selectors';
import { TCustomsDutyRate, TCustomsDutyRateListRequest } from 'redux/reducers/taxDuties/types';
import { getCountryListRequest } from 'redux/reducers/country/reducer';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { tableStructure } from './tableStructure';
import { ETaxDutiesTabGroupLabel } from '../../constants';

export const EuCustomsDutyRates: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);

	const list = useSelector(getCustomsDutyRateList);
	const listLoading = useSelector(getCustomsDutyRateListLoading);
	const countries = useSelector(getCountryListData);

	useEffect(() => {
		if (!countries) dispatch(getCountryListRequest());
	}, [countries, dispatch]);

	const params = useMemo(() => queryString.parse(search), [search]);

	const listRequest = useMemo<TCustomsDutyRateListRequest>(() => {
		const filters = (
			getFiltersFromNavigateParams(tableStructure, params) as TFilters
		).flatMap<TFilter>((filter) => {
			if (filter.fieldName !== 'originCode') return filter;
			const countryId = countries?.find((item) => item.isoAlpha2 === filter.value)?.id;
			if (!countryId) return [];
			return [
				{
					...filter,
					fieldName: 'countryId',
					name: 'countryId',
					value: String(countryId),
				},
			];
		});

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: tableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.ASC });

		return {
			pageNumber: params.page ? +params.page : 1,
			pageSize: PAGE_SIZE,
			searchString: String(params?.search ?? ''),
			filters,
			orderBy,
		};
	}, [params, countries]);

	useEffect(() => {
		dispatch(getCustomsDutyRateListRequest(listRequest));
	}, [dispatch, listRequest]);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TCustomsDutyRate>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	return (
		<EasTableWithFilter<TCustomsDutyRate>
			tableStructure={currentTableStructure}
			items={list}
			itemsIsLoading={listLoading}
			handleAction={() => undefined}
			onTableStructureChange={handleTableStructureChange}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant="body1" color="primary">
					Resources / Reference Tables / Tax &amp; Duties /{' '}
					<b>{ETaxDutiesTabGroupLabel.EU_CUSTOMS_DUTY_RATES}</b>
				</Typography>
			</Box>
		</EasTableWithFilter>
	);
};
